import React, {
  ForwardedRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from 'react';

import * as echarts from 'echarts';
import { EChartsType } from 'echarts';
import { ChartProps, ChartRef } from './_config/interface';
import { xAxisIntervalFormat } from './_config/plug';

const ChartInner: React.ForwardRefRenderFunction<ChartRef, ChartProps> = (
  {
    option,
    hasDataZoom,
    chartEventConfig,
    notMerge = true,
    showBarPeiName,
    chartType = '',
    onChartEarliestClick,
    setSelectedlegend,
    ifLineChartLegendOn = false,
    setLegendSelectedFalse,
    pieDataHighIndex,
  },
  ref: ForwardedRef<ChartRef>,
) => {
  const cRef = useRef<HTMLDivElement>(null);
  const cInstance = useRef<EChartsType>();
  const originOptionRef = useRef<any>();
  // const [cache, setCache] = useState('');
  const [echartXTextSubtractWidth, setEchartXTextSubtractWidth] = useState(0);
  const [startTime, setStartTime] = useState(0);

  const aXisTextWidth = useMemo(() => {
    if (showBarPeiName?.length > 0) {
      const widthTemp =
        Object.prototype.toString.call(showBarPeiName) === '[object Array]'
          ? showBarPeiName.length > 10
            ? 100
            : 350 - showBarPeiName.length * 26
          : 350;
      return widthTemp - echartXTextSubtractWidth;
    }
    return null;
  }, [showBarPeiName, echartXTextSubtractWidth]);

  // 初始化注册组件，监听 cRef 和 option 变化
  useEffect(() => {
    /**
     * 由于option中 存在Function类型的配置，Function类型配置更新后，JSON.stringify方法进行比较，无法感知Function类型的配置更新了，导致echarts没有重新渲染
     * 所以这里的优化暂时注释了
     * */
    // 减少 不必要渲染
    // if (cache === JSON.stringify(option)) {
    //   return
    // };
    // setCache(JSON.stringify(option));
    let optionTemp = option;
    try {
      if (cRef.current) {
        // 校验 Dom 节点上是否已经挂载了 ECharts 实例，只有未挂载时才初始化
        cInstance.current = echarts.getInstanceByDom(cRef.current);
        if (!cInstance.current) {
          cInstance.current = echarts.init(cRef.current, undefined, {
            renderer: 'canvas',
          });
          cInstance.current?.on('error', (err) => {
            console.log(err);
          });
          if (hasDataZoom && cInstance.current) {
            optionTemp = xAxisIntervalFormat(optionTemp, {
              formatter: (value: any) => (value ? value?.slice(11, 16) : ''),
              chart: cInstance.current,
            });
          }

          if (chartEventConfig && cInstance.current) {
            optionTemp = chartEventConfig(optionTemp, cInstance.current);
          }
        }

        // 柱状图，并且有必要做文案适应宽度的，才添加此属性
        if (showBarPeiName?.length > 0 && chartType == 'bar' && optionTemp && aXisTextWidth) {
          optionTemp = {
            ...optionTemp,
            xAxis: {
              ...optionTemp.xAxis,
              axisLabel: {
                ...optionTemp.xAxis?.axisLabel,
                width: aXisTextWidth,
              },
            },
          };
        }

        // 记录原始配置（含所有系列），用于 legend 交互时基于原始数据做筛选
        originOptionRef.current = optionTemp;
        // 设置配置项
        if (optionTemp) cInstance.current?.setOption(optionTemp, notMerge); // 第二个参数notMerge，默认为false，意为合并之前的option
        cInstance.current?.off('click');
        cInstance.current?.on('click', (...args) => {
          // console.log('clickclickclick', args);
          if (Array.isArray(args) && args?.length > 0) {
            if (
              args[0].componentType === 'markLine' &&
              args[0].name === 'Earliest Date' &&
              onChartEarliestClick instanceof Function
            ) {
              onChartEarliestClick?.(args[0]);
            }
          }
        });

        // Handle legend toggles for line and stackedLine charts to keep True Data scatter in sync with parent
        if (chartType === 'line' || chartType === 'stackedLine') {
          cInstance.current?.off('legendselectchanged');
          cInstance.current?.on('legendselectchanged', (params) => {
            const arrTemp: string[] = [];
            const selectedObj: Record<string, boolean> = (params as any)?.selected || {};
            for (const key in selectedObj) {
              if (selectedObj[key] === false) arrTemp.push(key);
            }
            setLegendSelectedFalse?.(arrTemp);

            // 根据 legend 选择，过滤系列：
            // 仅对 True Data 的散点（带 dbParentName）进行隐藏/显示随父系列联动；
            // 不移除折线系列本身，避免 legend 项被删除导致无法恢复。
            try {
              const baseOpt = originOptionRef.current || option;
              const baseSeries = Array.isArray(baseOpt?.series) ? baseOpt.series : [];
              // 不删除任何系列；仅当父系列被隐藏时，清空对应 True Data 散点的数据
              const mappedSeries = baseSeries.map((item: any) => {
                if (item?.type === 'scatter' && item?.dbParentName) {
                  if (arrTemp.includes(item.dbParentName)) {
                    return { ...item, data: [] };
                  }
                }
                return item;
              });
              // 仅更新系列数据（尤其是 True Data 散点的数据），不动 legend 和其他配置
              // 使用合并更新，避免覆盖/移除其它配置项
              cInstance.current?.setOption({ series: mappedSeries }, false);
            } catch (err) {
              console.log('legend filter error', err);
            }
          });
        }

        if (chartType == 'pie') {
          cInstance.current?.dispatchAction({
            type: 'highlight',
            seriesIndex: 0,
            dataIndex: pieDataHighIndex,
          });
        }
      }
    } catch (e) {
      console.log('echarts ---> error', e);
    }

  }, [cRef, option]);

  // 监听窗口大小变化重绘
  useEffect(() => {
    // console.log('window');
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [option]);

  // 重新适配大小并开启过渡动画
  const resize = () => {
    // console.log('resize');
    // 屏幕宽度变化时计算文案DOM要减去的宽度
    // 防抖
    try {
      if (showBarPeiName?.length > 0 && chartType == 'bar') {
        const currentTime = new Date().getTime();
        if (currentTime - startTime < 500) return;
        setStartTime(currentTime);
        // 1920 1260
        const innerWidth = window?.innerWidth || 0;
        // 根据屏幕宽度计算echarts x轴文案相适应要减去的宽度，避免文案堆叠显示
        const subtractWidth = innerWidth > 1260 ? (1920 - innerWidth) * (40 / (1920 - 1260)) : 40;
        setEchartXTextSubtractWidth(Math.round(subtractWidth));
      }

      cInstance.current?.resize({
        animation: { duration: 300 },
      });
    } catch (error) {
      console.log('echarts ---> error', error);
    }
  };

  // 获取实例
  const instance = () => cInstance.current;

  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance,
  }));

  return <div ref={cRef} style={{ width: '100%', height: '100%' }} />;
};

const Chart = React.forwardRef(ChartInner);

export default Chart;
